import React, { Fragment } from 'react';
import { styled } from '@components-shared/src/stitches';
import { Divider } from '@components/main-components/divider';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { useAuth } from '@context/auth/base';
import { useHeaderStore } from '@store/header';
import { NAV_LINKS } from '@utils/constants';
import { useShouldShowReferralEarningsIndicator } from '../Notification';
import { LinkIndicator, NavLink, NewNavLink } from '../styles';
import { CollectorSignedInOptions } from './CollectorSignedInOptions';
import { ForArtists } from './ForArtists';

type NavMenuOptions = {
  title: string;
  key: string;
  href: Pathname;
  hideMobile?: boolean;
  notificationIndicator?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
};

const ADMIN_OR_ARTIST_RELATIONS_OPTIONS: Array<NavMenuOptions> = [
  {
    title: 'Admin',
    key: 'admin',
    href: '/admin',
  },
  {
    title: 'Artist Applications',
    key: 'artist-application',
    href: NAV_LINKS.AdminArtistApplications,
  },
];

const ARTIST_OPTIONS_WITH_PROFILE: Array<NavMenuOptions> = [
  {
    title: 'My Releases',
    key: 'artist-releases',
    href: '/dashboard/releases',
  },
];

export const NavLinks = ({ variant }: { variant: 'desktop' | 'mobile' }) => {
  const { authUser, isConnectingAPIUser } = useAuth();

  const { close } = useHeaderStore();

  const isEitherAdminOrRelations = !!authUser?.roles.isAdmin || !!authUser?.roles.isArtistRelations;
  const hasArtistRole = !!authUser?.artist;

  const adminOrArtistRelationsMenuOptions = ADMIN_OR_ARTIST_RELATIONS_OPTIONS.map(option => {
    return (
      <Fragment key={option.key}>
        {isConnectingAPIUser ? (
          <LoadingSkeleton />
        ) : (
          <AccessibleLink absoluteUri={option.href} type="external" onClick={close}>
            <NavLink variant={variant}>{option.title}</NavLink>
          </AccessibleLink>
        )}
      </Fragment>
    );
  });

  const shouldShowReferralEarningsIndicator = useShouldShowReferralEarningsIndicator();

  const WITHDRAW_OPTIONS: Array<NavMenuOptions> = [
    {
      title: 'My Earnings',
      key: 'withdraw',
      href: '/dashboard/earnings',
      notificationIndicator: shouldShowReferralEarningsIndicator,
    },
  ];

  const ARTIST_DASHBOARD: Array<NavMenuOptions> = [
    {
      title: 'Artist Dashboard',
      key: 'artist-dashboard',
      href: '/dashboard/releases',
    },
  ];

  const artistWithProfileOptions =
    variant === 'desktop'
      ? [...ARTIST_DASHBOARD]
      : [...ARTIST_OPTIONS_WITH_PROFILE, ...WITHDRAW_OPTIONS];

  const artistOptionsToShow = hasArtistRole
    ? artistWithProfileOptions.filter(({ hideMobile }) => {
        if (variant === 'mobile' && hideMobile) {
          return false;
        }
        return true;
      })
    : [];

  const artistMenuOptions = artistOptionsToShow.map(option => {
    const pathnameUri = option.href;

    return (
      <Fragment key={option.key}>
        {isConnectingAPIUser ? (
          <LoadingSkeleton />
        ) : (
          <AccessibleLink pathnameUri={pathnameUri} onClick={close}>
            <NewNavLink>
              {' '}
              {option.icon}
              {option.title}
              {option.notificationIndicator && <LinkIndicator variant={variant} />}
            </NewNavLink>
          </AccessibleLink>
        )}
      </Fragment>
    );
  });

  return (
    <>
      <NavDivider orientation="horizontal" />
      {artistMenuOptions}
      {hasArtistRole && (
        <AccessibleLink pathnameUri={NAV_LINKS.Community} onClick={close} className="md:hidden">
          <NewNavLink>My Community</NewNavLink>
        </AccessibleLink>
      )}
      <CollectorSignedInOptions variant={variant} />

      {isEitherAdminOrRelations && (
        <>
          <NavDivider orientation="horizontal" />
          {adminOrArtistRelationsMenuOptions}
        </>
      )}
      {!hasArtistRole && <ForArtists navLoading={isConnectingAPIUser} closeNav={close} />}
    </>
  );
};

export const NavDivider = styled(Divider, {
  backgroundColor: '$neutral100',
  marginBottom: 0,
});

const LoadingSkeleton = styled(Skeleton, {
  borderRadius: 6,
  width: '100%',
  height: 32,

  '@tablet': {
    width: 152,
    height: 24,
  },
});
